import PropTypes from 'prop-types';
import { isUndefined, omitBy } from 'lodash';
import { ConfigProvider } from 'antd';
import { Locale } from '@fixter/i18n';
import { queryCmsContent } from '$server/modules/cms';
import deferHydration from '$util/deferHydration';
import MainLayout from '$layout/MainLayout';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getReassuranceQuery } from '$components/composite/Reassurance/gql';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { getTeamSupportSection } from '$quantum/organisms/TeamSupportSection/gql';
import { getPromotionBannerQuery } from '$quantum/molecules/PromotionBanner/gql';
import { GET_CUSTOMER_SERVICE_CMS_QUERY } from '$cms/gql';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { useAppContext } from '$util/AppContext';
import { getShortTextByKey } from '$cms/getDataByKey';
import { getMetaTitle } from '$components/pages/gql';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { WithResponsiveGlobalStyles } from '$util/theme/withResponsive';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'how-it-works';

const HowItWorksSection = deferHydration(() => import('$components/HowItWorksSection'), {
  hydrationId: '$components/HowItWorksSection',
  trigger: 'view',
});

const OurMechanicsSection = deferHydration(() => import('$components/OurMechanicsSection'), {
  hydrationId: '$components/OurMechanicsSection',
  trigger: 'view',
});

const GarageNetworkSection = deferHydration(() => import('$components/GarageNetworkSection'), {
  hydrationId: '$components/GarageNetworkSection',
  trigger: 'view',
});

const TryFixterSection = deferHydration(() => import('$components/TryFixterSection'), {
  hydrationId: '$components/TryFixterSection',
  trigger: 'view',
});

const ReassuranceBlueIconsTheme = deferHydration(() => import('$components/ReassuranceBlueIconsTheme'), {
  hydrationId: '$components/ReassuranceBlueIconsTheme',
  trigger: 'view',
});

const TeamSupportSection = deferHydration(() => import('$quantum/organisms/TeamSupportSection'), {
  hydrationId: '$components/TeamSupportSection',
  trigger: 'view',
});

function HowItWorksPage({ ...props }) {
  const AppContext = useAppContext();
  const {
    cmsContent: { dictionary },
  } = AppContext;
  const getShortText = getShortTextByKey.bind(dictionary);
  const pageTitle = getShortText('metaTitle.howItWorks', 'How it works | Fixter');

  return (
    <ConfigProvider
      theme={{
        cssVar: true,
        hashed: false,
        token: { fontFamily: 'Rubik, sans-serif' },
      }}
    >
      <MainLayout
        {...props}
        seo={{ noindex: false, nofollow: false, title: pageTitle }}
        className="HowItWorksPage"
      >
        <WithResponsiveGlobalStyles />
        <HowItWorksSection {...props} />
        <ReassuranceBlueIconsTheme />
        <OurMechanicsSection />
        <GarageNetworkSection />
        <TryFixterSection {...props} />
        <TeamSupportSection source={PAGE_NAME} />
      </MainLayout>
    </ConfigProvider>
  );
}

export const getServerSideProps = async function ({ req, res, query: urlQuery, preview: isPreviewMode }) {
  const locale = getLocaleFromHostname(req.headers.host || '');
  if (locale !== Locale.EN_GB) {
    return {
      notFound: true,
    };
  }

  const serverParams = {
    pathname: 'how-it-works',
    pageType: [],
    pageTypeFromPath: 'how-it-works',
    pageMeta: {},
  };
  const { pathname, pageType, pageTypeFromPath, pageMeta } = serverParams;

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getNavbarQuery,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
      getReassuranceQuery,
      GET_CUSTOMER_SERVICE_CMS_QUERY,
      getTeamSupportSection,
      getPromotionBannerQuery,
      getMetaTitle,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  const props = omitBy(
    {
      serverParams,
      pathname,
      pageType,
      pageMeta,
      pageTypeFromPath,
      initChat: false,
      cmsContent,
    },
    isUndefined
  );

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

  return { props };
};

HowItWorksPage.propTypes = {
  pathname: PropTypes.string.isRequired,
  pageType: PropTypes.array.isRequired,
  pageMeta: PropTypes.object.isRequired,
};

export default HowItWorksPage;
